import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

export default function AdminBypassPanel() {
  const { activateAdminBypass, deactivateAdminBypass, isBypassActive } = useAuth();
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [keySequence, setKeySequence] = useState('');

  // Secret keyboard shortcut: type "admin" anywhere on the page
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const newSeq = (keySequence + e.key).slice(-5);
    setKeySequence(newSeq);
    if (newSeq === 'admin') {
      setVisible(true);
      setKeySequence('');
    }
  }, [keySequence]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const success = activateAdminBypass(code);
    if (success) {
      setMessageType('success');
      setMessage('✅ Admin bypass activated! You now have full access.');
      setCode('');
      setTimeout(() => {
        setVisible(false);
        setMessage('');
        window.location.reload();
      }, 1500);
    } else {
      setMessageType('error');
      setMessage('❌ Invalid code. Please try again.');
    }
  };

  const handleDeactivate = () => {
    deactivateAdminBypass();
    setMessage('');
    setVisible(false);
    window.location.reload();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 relative">
        <button
          onClick={() => { setVisible(false); setMessage(''); setCode(''); }}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-shield-keyhole-line text-2xl text-white"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Author Access</h2>
          <p className="text-sm text-gray-500 mt-1">Enter your secret code to unlock all content</p>
        </div>

        {isBypassActive ? (
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
              <p className="text-green-700 font-medium text-sm">
                <i className="ri-checkbox-circle-fill mr-2"></i>
                Admin bypass is currently active
              </p>
              <p className="text-green-600 text-xs mt-1">You have full access to all content</p>
            </div>
            <button
              onClick={handleDeactivate}
              className="w-full px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg font-medium hover:bg-red-100 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Deactivate Bypass
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter secret code"
                autoFocus
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent tracking-widest text-center font-mono"
              />
            </div>

            {message && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm text-center ${
                  messageType === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              className="w-full px-4 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors text-sm cursor-pointer whitespace-nowrap"
            >
              Unlock Access
            </button>
          </form>
        )}

        <p className="text-center text-xs text-gray-400 mt-5">
          Press <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono">a</kbd>
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono ml-0.5">d</kbd>
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono ml-0.5">m</kbd>
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono ml-0.5">i</kbd>
          <kbd className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-mono ml-0.5">n</kbd>
          {' '}to open this panel
        </p>
      </div>
    </div>
  );
}
